import { useState } from 'react';
import { Search, Wallet } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import { usePaymentMethodsQuery, usePaymentsQuery, useVoidPaymentMutation, type Payment } from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDateTime, formatMoney } from '@/lib/format';
import {
  Alert, Badge, Button, Card, EmptyState, ErrorState, Input, LoadingState, Modal,
  PageHeader, Pagination, Select, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { SummaryCards } from '@/components/SummaryCards';
import { ALL_TIME, DateRangeFilter, type DateRange } from '@/components/DateRangeFilter';

export function PaymentsPage() {
  const format = useFormat();

  const [page, setPage] = useState(1);
  const [range, setRange] = useState<DateRange>(ALL_TIME);
  const [search, setSearch] = useState('');
  const [methodId, setMethodId] = useState('');
  const [voiding, setVoiding] = useState<Payment | null>(null);
  const [reason, setReason] = useState('');
  const [failure, setFailure] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch, isFetching } = usePaymentsQuery({
    from: range.from || undefined,
    to: range.to || undefined,
    page,
    limit: 25,
    search: search || undefined,
    methodId: methodId || undefined,
  });
  const { data: methods } = usePaymentMethodsQuery();
  const [voidPayment, voidState] = useVoidPaymentMutation();

  const confirmVoid = async (): Promise<void> => {
    if (!voiding) return;
    setFailure(null);
    try {
      await voidPayment({ id: voiding.id, reason }).unwrap();
      setVoiding(null);
      setReason('');
    } catch (voidError) {
      setFailure(apiErrorMessage(voidError, 'That payment could not be voided'));
    }
  };

  return (
    <>
      <PageHeader
        title="Payments"
        description="Money received from customers, and what each payment settled."
      />

      {failure && (
        <div className="mb-4">
          <Alert tone="danger">{failure}</Alert>
        </div>
      )}

      {data?.summary && (
        <SummaryCards
          stats={[
            { label: 'Payments', value: String(data.summary.count), hint: 'in this period' },
            { label: 'Received', value: formatMoney(String(data.summary.received), format) },
            {
              label: 'On account',
              value: formatMoney(String(data.summary.unallocated), format),
              hint: 'taken, not yet against a sale',
            },
            {
              label: 'Provider fees',
              value: formatMoney(String(data.summary.fees), format),
              hint: 'withheld by the provider',
            },
          ]}
        />
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-subtle p-4">
          <DateRangeFilter
            value={range}
            onChange={(next) => {
              setRange(next);
              setPage(1);
            }}
          />
          <div className="min-w-48 flex-1">
            <Input
              label="Search"
              icon={Search}
              placeholder="Payment number or reference"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="w-full sm:w-48">
            <Select
              label="Method"
              value={methodId}
              onChange={(event) => {
                setMethodId(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All methods</option>
              {methods?.map((method) => (
                <option key={method.id} value={method.id}>{method.name}</option>
              ))}
            </Select>
          </div>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={Wallet}
            title={search || methodId ? 'Nothing matches' : 'No payments yet'}
            description="Payments are recorded against a sale, or taken at the till."
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Number</Th>
                  <Th>Received</Th>
                  <Th>Customer</Th>
                  <Th>Method</Th>
                  <Th>Reference</Th>
                  <Th numeric>Amount</Th>
                  <Th numeric>Unallocated</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className={isFetching ? 'opacity-60' : undefined}>
                {data?.items.map((payment) => (
                  <tr key={payment.id} className="hover:bg-sunken">
                    <Td>
                      <span className="font-medium">{payment.number}</span>
                      {payment.voidedAt && (
                        <Badge tone="neutral" className="ml-2">voided</Badge>
                      )}
                    </Td>
                    <Td className="text-secondary">
                      {formatDateTime(payment.receivedAt, format)}
                    </Td>
                    <Td className="text-secondary">{payment.customer?.name ?? 'Walk-in'}</Td>
                    <Td className="text-secondary">{payment.method.name}</Td>
                    <Td className="text-secondary">{payment.reference ?? '—'}</Td>
                    <Td numeric className={payment.voidedAt ? 'text-muted line-through' : undefined}>
                      {formatMoney(payment.amount, format)}
                    </Td>
                    <Td numeric className="text-secondary">
                      {Number(payment.unallocated) > 0
                        ? formatMoney(payment.unallocated, format)
                        : '—'}
                    </Td>
                    <Td className="text-right">
                      {!payment.voidedAt && (
                        <Can permission={P.PAYMENT_DELETE}>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setFailure(null);
                              setVoiding(payment);
                            }}
                          >
                            Void
                          </Button>
                        </Can>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {data && (
              <Pagination
                page={data.meta.page}
                totalPages={data.meta.totalPages}
                total={data.meta.total}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <Modal
        open={voiding !== null}
        onClose={() => setVoiding(null)}
        title="Void this payment?"
        description="The allocations are reversed and the customer is re-debited, so whatever this settled becomes owing again."
        footer={
          <>
            <Button variant="outline" onClick={() => setVoiding(null)}>Cancel</Button>
            <Button
              variant="danger"
              loading={voidState.isLoading}
              disabled={!reason}
              onClick={() => void confirmVoid()}
            >
              Void payment
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {voiding && (
            <p className="text-sm text-secondary">
              {voiding.number} · {formatMoney(voiding.amount, format)} by {voiding.method.name}
            </p>
          )}
          <Input
            label="Reason"
            placeholder="Cheque bounced"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
      </Modal>
    </>
  );
}
